import React, { useState, useEffect } from "react";
import moment from "moment";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import t from "../../helpers/t";
import { Biscuit } from "../../../utils";
import EditableInput from "../../helpers/EditableInput";

import { fileManagerActions, alertsActions } from "inbooma-api";

// import { rbac } from './index';
// @todo Implement rbac, if necessary (right now view is only accessible by admin)
// @todo style select list https://react-select.com/styles

let editedEntity = {}; // === The body of PATCH /entity/xy, initialized further down

export default (props) => {
  const entityType = typeof props.entity.user_docs !== "undefined" ? "folder" : "file",
    entity = props.entity,
    created = moment(entity.created);

  const [permissionTargetKey, setPermissionTargetKey] = useState("");
  const [entityNameEditMode, setEntityNameEditMode] = useState(false);
  const [entityWasEdited, setEntityWasEdited] = useState(0); // Mutable integer ensuring re-render

  if (entityType === "file") {
    // Suddenly, in the wild grasses of Pokemon town, a
    // non-store mutation appears:
    entity.name = entity.description;
  }

  useEffect(() => {
    if (permissionTargetKey === "") {
      // Set internal state

      if (entityType === "folder") {
        // Pick patchable attributes from folder
        editedEntity = (({ all_roles, locations, roles }) => ({ all_roles, locations, roles }))(
          entity,
        );

        // We assume "Folder-has-one-target"
        if (entity.role !== null && entity.role.length >= 1) {
          setPermissionTargetKey("role");
        }
        if (entity.locations !== null && entity.locations.length >= 1) {
          setPermissionTargetKey("locations");
        }
        if (entity.all_roles) {
          setPermissionTargetKey("all_roles");
        }
      } else {
        // Pick patchable attributes from document
        editedEntity = (({ lang, tags, folders }) => ({ lang, tags, folders }))(entity);
      }
    }
  }, [permissionTargetKey, entityType, entity]);

  /**
   * Recursively checks for any user_docs in any child folder
   *
   * @param {Object} folder
   * @return {bool}
   */
  const folderHasAnyChildUserDoc = (folder) => {
    let result = false;

    if (typeof folder.user_docs !== "undefined" && folder.user_docs.length >= 1) {
      result = true;
    }

    if (!result && typeof folder.child_folders !== "undefined") {
      folder.child_folders.forEach((_folder) => {
        if (!folderHasAnyChildUserDoc(_folder)) {
          result = true;
          return;
        }
      });
    }

    return result;
  };

  const onPermissionTargetKeyChange = (ev) => {
    const value = ev.currentTarget.value;

    setPermissionTargetKey(value);

    switch (value) {
      case "all_roles":
        editedEntity["all_roles"] = true;
        editedEntity["role"] = null;
        editedEntity["locations"] = [];
        break;
      case "locations":
        editedEntity["all_roles"] = false;
        editedEntity["role"] = null;
        editedEntity["locations"] = entity.locations.map((location) => {
          return { id: location.value };
        });
        break;
      case "role":
        editedEntity["all_roles"] = false;
        editedEntity["role"] = entity.role === null ? "admin" : entity.role;
        editedEntity["locations"] = [];
    }

    setEntityWasEdited(entityWasEdited + 1);

    return false;
  };

  const patchEntity = () => {
    if (entityType === "file") {
      delete editedEntity["tags"];
    }

    props.dispatch(
      fileManagerActions[entityType === "folder" ? "editFolder" : "editDocument"]({
        id: Number(entity.id),
        token: Biscuit.get("token"),
        body: {
          ...editedEntity,
        },
      }),
    );

    props.dispatch(
      alertsActions.addAlert({
        message: t("updated_${a}", { a: t(entityType) }, "capital"),
        icon: "fi fi-check",
      }),
    );

    if (entityType === "file") {
      // Get new list of tags for tagcloud
      // @todo Move into saga
      setTimeout(() => {
        props.dispatch(
          fileManagerActions.getTags({
            token: Biscuit.get("token"),
            namespace: "UserDoc",
          }),
        );
      }, 1000);
    }

    setEntityWasEdited(0);
  };

  const onFileLangChange = (ev) => {
    const value = ev.currentTarget.value;

    editedEntity["lang"] = value;
    entity.lang = ev.currentTarget.value;
    setEntityWasEdited(entityWasEdited + 1);
  };

  const onChangeFolderRole = (ev) => {
    const value = ev.currentTarget.value;

    editedEntity["role"] = value;
    entity.role = value;
    setEntityWasEdited(entityWasEdited + 1);
  };

  const onChangeFolderLocations = (locations) => {
    editedEntity["locations"] = locations.map((location) => {
      return { id: location.value };
    });
    entity.locations = locations;
    setEntityWasEdited(entityWasEdited + 1);
  };

  const onChangeTags = (tags) => {
    // A smooth string containing the tags is sufficient here
    if (tags === null || tags.length === 0) {
      editedEntity["tag_list"] = [];
      entity.tags = [];
    } else {
      editedEntity["tag_list"] = tags.map((tag) => tag.label).join(",");
      entity.tags = tags;
    }
    setEntityWasEdited(entityWasEdited + 1);
  };

  const onChangeFolders = (folders) => {
    if (folders === null || folders.length === 0) {
      // Minimum required input is 1 item
      return;
    }
    editedEntity["folders"] = folders.map((folder) => {
      return { id: folder.value };
    });
    entity.folders = folders;
    setEntityWasEdited(entityWasEdited + 1);
  };

  const deleteEntity = () => {
    if (!confirm(t("really_remove"))) {
      return;
    }

    if (entityType === "folder") {
      if (folderHasAnyChildUserDoc(entity)) {
        alert(t("remove_folder_forbidden"));
        return;
      }

      props.dispatch(
        fileManagerActions.deleteFolder({
          id: Number(entity.id),
          token: Biscuit.get("token"),
        }),
      );

      // @todo .then:
      props.dispatch(
        alertsActions.addAlert({
          message: t("removed_${a}", { a: t("folder") }),
          icon: "fi fi-trash",
        }),
      );
    } else {
      const folder_id =
        typeof entity._joinData !== "undefined" ? entity._joinData.folder_id : entity.folders[0].id;

      props.dispatch(
        fileManagerActions.deleteDocument({
          token: Biscuit.get("token"),
          id: Number(entity.id),
          body: {
            id: Number(entity.id),
            folders: [{ id: folder_id }],
          },
          s3: {
            client_id: Number(Biscuit.get("user.client_id")),
            type: "UserDoc",
            location: 0,
            name: entity.file,
          },
        }),
      );

      // @todo .then:
      props.dispatch(
        alertsActions.addAlert({
          message: t("removed_${a}", { a: t("document") }),
          icon: "fi fi-trash",
        }),
      );
    }

    props.setTagFilter(null); // @todo If file, remove from user_doc store
    props.onClose();
  };

  const onSubmitNewEntityName = (newValue) => {
    setEntityNameEditMode(false);

    if (newValue == entity.name) {
      return;
    }

    if (entityType === "folder") {
      props.dispatch(
        fileManagerActions.editFolder({
          id: Number(entity.id),
          token: Biscuit.get("token"),
          body: {
            name: newValue,
          },
        }),
      );

      props.dispatch(
        alertsActions.addAlert({
          message: "Folder name updated",
          icon: "fi fi-check",
        }),
      );
    } else {
      props.dispatch(
        fileManagerActions.editDocument({
          id: Number(entity.id),
          token: Biscuit.get("token"),
          body: {
            description: newValue,
          },
        }),
      );

      // @todo:
      entity.description = newValue;

      props.dispatch(
        alertsActions.addAlert({
          message: "File name updated",
          icon: "fi fi-check",
        }),
      );
    }

    // @todo Use updated model from dispatcher
    entity.name = newValue;
  };

  //console.log('FOLDERS', props.folders, typeof entity.folders, entity);

  // @todo created_by, modified[_by]
  return (
    <aside className="filemanager-entity-detail medium-3 column">
      <header className="inb-header off-canvas__header filemanager-entity-detail__header row clearfix">
        {entityType === "folder" ? (
          <i className="fi fi-folder"></i>
        ) : (
          <i className="fi fi-page"></i>
        )}

        {entityNameEditMode ? (
          <EditableInput
            value={entity.name}
            inputType="input"
            isStateless="true"
            onSubmit={onSubmitNewEntityName}
            onClose={() => setEntityNameEditMode(false)}
          />
        ) : (
          <div className="inb-page-title">
            <h1 onClick={() => setEntityNameEditMode(true)}>
              {/*@todo entity.name.slice(0,25) + '...' */}
              {entity.name}
            </h1>

            <div className="inb-page-title__actions">
              <i
                title={t("edit") + " " + t("name")}
                className="fi fi-pencil"
                onClick={() => setEntityNameEditMode(true)}
              ></i>
            </div>
          </div>
        )}

        <div className="off-canvas__actions column">
          <button
            type="button"
            className="button hollow secondary right button-abort round"
            title={t("delete_${a}", { a: t(entityType === "folder" ? "folder" : "file") })}
            onClick={deleteEntity}
          >
            <i className="fi fi-trash" style={{ marginLeft: "0" }}></i>
          </button>

          <button
            type="button"
            className="button hollow secondary right button-abort round"
            title={t("close_window")}
            onClick={props.onClose}
          >
            <i className="fi fi-x"></i>
          </button>
        </div>
      </header>

      <p className="off-canvas__subtitle off-canvas__header-region row">
        {t("created_at")} {created.format("Do MMMM YYYY, HH:mm")}
        {entityType === "folder" && (
          <>
            | {entity.user_docs.length} {t("document_s")}
            {/*@todo | Size: 3,2 MiB*/}
          </>
        )}
      </p>

      {entityType === "folder" ? (
        <div className="filemanager-entity-detail__content small-12 column">
          <label htmlFor="folder-permission-target-key">
            {t("folder_accessible_by")}

            <select
              id="folder-permission-target-key"
              name="folder-permission-target-key"
              value={permissionTargetKey}
              onChange={onPermissionTargetKeyChange}
            >
              <option value="all_roles">{t("all", null, "capital")}</option>
              <option value="role">{t("role", null, "capital")}</option>
              <option value="locations">{t("locations", null, "capital")}</option>
            </select>
          </label>

          {permissionTargetKey === "locations" && (
            <Select
              value={entity.locations.map((location) =>
                typeof location.id !== "undefined"
                  ? { value: location.id, label: location.name }
                  : location,
              )}
              isMulti="true"
              onChange={onChangeFolderLocations}
              placeholder={t("select_${a}", { a: t("locations") })}
              options={props.locations.map((location) => {
                return { value: location.id, label: location.name };
              })}
            />
          )}

          {permissionTargetKey === "role" && (
            <select
              id="folder-permission-target-value"
              name="folder-permission-target-value"
              value={entity.role ? entity.role : ""}
              onChange={onChangeFolderRole}
            >
              <option value="admin">{t("role_admin")}</option>
              <option value="manager">{t("role_manager")}</option>
              <option value="manager-location">{t("role_manager-location")}</option>
              <option value="user">{t("role_user")}</option>
              {/* <option value="guide">{t('role_guide')}</option> */}
              <option value="mediator">{t("role_mediator")}</option>
            </select>
          )}
        </div>
      ) : (
        <div className="filemanager-entity-detail__content small-12 column">
          <label htmlFor="file-lang">
            {t("language")}

            <select
              id="file-lang"
              name="file-lang"
              placeholder={t("change_language")}
              value={entity.lang}
              onChange={onFileLangChange}
            >
              <option value="null">- {t("all", null, "capital")} -</option>
              <option value="de_DE">{t("de_DE")}</option>
              <option value="en_GB">{t("en_GB")}</option>
              <option value="fr_FR">{t("fr_FR")}</option>
            </select>
          </label>
          {/*styles={{
                        control: provided => ({
                            ...provided,
                            minHeight: "10px",
                          }),
                          indicatorsContainer: provided => ({
                            ...provided,
                            height: "10px"
                          }),
                          clearIndicator: provided => ({
                            ...provided,
                            padding: "1px"
                          }),
                          dropdownIndicator: provided => ({
                            ...provided,
                            padding: "1px"
                          })
                    }}*/}

          {/*https://react-select.com/creatable*/}
          <label htmlFor="file-tags">
            {t("tags", null, "capital")}

            <CreatableSelect
              value={
                typeof entity.tags === "undefined"
                  ? []
                  : entity.tags.map((tag) =>
                      typeof tag.id !== "undefined" ? { value: tag.id, label: tag.label } : tag,
                    )
              }
              isMulti="true"
              onChange={onChangeTags}
              theme={(theme) => ({
                ...theme,
                borderRadius: "3px",
                colors: {
                  ...theme.colors,
                  primary25: "#EB6A3A",
                  primary: "#EB6A3A",
                },
              })}
              placeholder={t("select_${a}", { a: t("tag") })}
              options={props.tags.map((tag) => {
                return { value: tag.id, label: tag.label };
              })}
            />
          </label>

          <br />
          {typeof entity.folders !== "undefined" && (
            <label htmlFor="file-folders">
              {t("link_folders", null, "capital")}

              <Select
                value={entity.folders.map((folder) =>
                  typeof folder.id !== "undefined"
                    ? { value: folder.id, label: folder.name }
                    : folder,
                )}
                isMulti="true"
                onChange={onChangeFolders}
                placeholder={t("select_${a}", { a: t("folder") })}
                options={props.folders.map((folder) => {
                  return { value: folder.id, label: folder.name };
                })}
              />
            </label>
          )}
        </div>
      )}

      {entityWasEdited >= 1 && (
        <div className="small-12 column">
          <button
            className="button"
            type="button"
            style={{ marginTop: "1rem" }}
            onClick={() => patchEntity()}
          >
            {t("save")}
          </button>
        </div>
      )}
    </aside>
  );
};
